import React from "react";
import { Activity, Calendar, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { EmptyState } from "../../../../components/shared/loading";
import FootballDesktopGameCard from "./FootballDesktopGameCard";

const FixturesView = ({
  fixtures,
  loading,
  error,
  onRetry,
  pagination,
  onPageChange,
  onToggleSelection,
  isGameSelected,
  isGameFrozen,
  getGamePrediction,
  formatTime,
  onViewOdds
}) => {
  if (error) {
    return (
      <EmptyState
        title="Failed to load fixtures"
        description={error}
        action={
          <button
            onClick={onRetry}
            className="px-4 py-2 bg-emerald-500/20 text-emerald-300 rounded-lg"
          >
            Try Again
          </button>
        }
      />
    );
  }

  if (!fixtures || fixtures.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <EmptyState
          title="No fixtures found"
          description="Try selecting a different date or league"
          icon={Calendar}
        />
      </div>
    );
  }

  const hasPagination = pagination && pagination.totalPages > 1;
  const pageFixtures = hasPagination
    ? fixtures.slice(
        (pagination.currentPage - 1) * pagination.perPage,
        pagination.currentPage * pagination.perPage
      )
    : fixtures;

  return (
    <div className="space-y-6">

      <div className="space-y-6 border border-white/10 rounded-sm bg-white/[0.02]">
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <h2 className="text-2xl font-black uppercase tracking-tight text-white flex items-center gap-3">
            Games ({fixtures.length})
          </h2>
          {hasPagination && (
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-gray-500">
              Page {pagination.currentPage} of {pagination.totalPages}
            </div>
          )}
        </div>

        <div className="p-6">
          {loading ? (
            <div className="border border-white/10 rounded-sm p-12 text-center bg-black">
              <RefreshCw className="animate-spin mx-auto mb-4 text-white" size={32} />
              <p className="text-xs font-mono uppercase tracking-[0.3em] text-gray-400">
                Loading games...
              </p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {pageFixtures.map((fixture) => {
                const fixtureWithId = {
                  ...fixture,
                  id: fixture?.fixture?.id || fixture?.id
                };
                return (
                <FootballDesktopGameCard
                  key={fixtureWithId.id}
                  fixture={fixtureWithId}
                  isSelected={isGameSelected?.(fixtureWithId)}
                  isFrozen={isGameFrozen?.(fixtureWithId)}
                  onToggleSelection={onToggleSelection}
                  onViewOdds={onViewOdds}
                  prediction={getGamePrediction ? getGamePrediction(fixtureWithId.id) : null}
                  formatTime={formatTime}
                />
              );
              })}
            </div>
          )}
        </div>

        {hasPagination && (
          <div className="p-6 border-t border-white/10 flex justify-center">
            <div className="flex items-center gap-3">
              <button
                onClick={() => onPageChange(Math.max(1, pagination.currentPage - 1))}
                disabled={pagination.currentPage === 1}
                className="px-3 py-2 border border-white/10 text-white text-xs font-mono uppercase tracking-widest rounded-sm bg-transparent hover:border-white/30 disabled:opacity-40 transition-all"
              >
                <ChevronLeft size={16} />
              </button>

              <span className="px-4 py-2 text-xs font-mono uppercase tracking-[0.3em] text-gray-400">
                {pagination.currentPage} / {pagination.totalPages}
              </span>

              <button
                onClick={() => onPageChange(Math.min(pagination.totalPages, pagination.currentPage + 1))}
                disabled={pagination.currentPage === pagination.totalPages}
                className="px-3 py-2 border border-white/10 text-white text-xs font-mono uppercase tracking-widest rounded-sm bg-transparent hover:border-white/30 disabled:opacity-40 transition-all"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FixturesView;
